'use client';

import type { TextAndServiceProps } from '@/core/types';

import React, { useMemo, useState } from 'react';

import { useTranslate } from '@/core/locales';
import { fNumber } from '@/core/utils/format-number';
import { TRANSLATION_NAMESPACES } from '@/core/constants';
import getCurrencySymbolByCode from '@/core/utils/get-currency-symbol';

import { Iconify } from '@/components/iconify';
import { Tooltip } from '@/components/ui/Tooltip';
import { BundlesDurations } from '@/components/bundles';
import { Box, Button, Container, Typography } from '@/components/ui';

import { RouterLink } from '@/routes/components';

export default function TextAndService({
  title,
  description,
  buttons = [],
  service,
  className = '',
}: TextAndServiceProps) {
  const { t } = useTranslate(TRANSLATION_NAMESPACES.COMMON);

  const isReverse = className.includes('col-reverse');

  const durations = service?.durations || [];
  const unit = service?.data as any;

  const [activeDuration, setActiveDuration] = useState(durations.length > 0 ? durations[0].id : '');

  const currentTariff = useMemo(() => {
    if (!unit?.tariffs) return undefined;
    return unit.tariffs.find((p: any) => p.id === activeDuration) || unit.tariffs[0];
  }, [unit, activeDuration]);

  const handleDurationChange = (durationId: string) => {
    setActiveDuration(durationId);
  };

  const currencySymbol = getCurrencySymbolByCode(currentTariff?.currencyCode);

  const getFeatureText = (feature: any, limitations?: any[]): string => {
    const limitation = limitations?.find((l) => l.featureId === feature.id);

    const durationUnitMap: Record<string, string> = {
      day: t('features.durations.day'),
      month: t('features.durations.month'),
      year: t('features.durations.year'),
      unlimited: t('features.durations.unlimited'),
    };

    const translatedDurationUnit = limitation?.durationUnit
      ? durationUnitMap[limitation.durationUnit]
      : undefined;

    return t(`features.${feature.slug}`, {
      count: limitation?.value ?? 0,
      durationUnit: translatedDurationUnit,
    });
  };

  const features: any[] = unit?.features ?? [];

  const renderDurations = () => (
    <BundlesDurations
      durations={durations}
      activeDuration={activeDuration}
      handleDurationChange={handleDurationChange}
    />
  );

  return (
    <Box className={`py-8 md:py-[120px] bg-light-gray/56 ${className}`}>
      <Container className="overflow-hidden">
        <Box
          className={`flex flex-col ${isReverse ? 'xl:flex-row-reverse' : 'xl:flex-row'} gap-6 xl:gap-12 items-center`}
        >
          <Box className="w-full xl:w-1/2">
            {title && (
              <Typography
                variant="h3"
                className="uppercase md:text-2xl font-bold mb-2 lg:mb-6 text-primary"
              >
                {title}
              </Typography>
            )}

            {description && (
              <Box
                className="text-sm md:text-base [&>p]:mb-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-3"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}

            {buttons && buttons.length > 0 && (
              <Box className="flex justify-start mt-2 lg:mt-6 gap-3">
                {buttons.map((button, index) => (
                  <Button
                    key={index}
                    href={button.link}
                    component={RouterLink}
                    variant={button.variant}
                    className={`min-w-[150px] md:min-w-[160px] px-2 md:px-4 text-sm ${
                      index === 1 ? '!text-black' : ''
                    } ${button.className}`}
                  >
                    {button.text}
                  </Button>
                ))}
              </Box>
            )}
          </Box>

          <Box className="w-full xl:w-1/2">
            {unit && (
              <Box className="relative p-6 px-4 md:px-6 rounded-4xl bg-white border border-violet  shadow-md transition-shadow duration-300 flex flex-col h-full">
                <Box className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <Box className="w-full sm:w-auto overflow-x-auto">
                    {durations.length > 0 && renderDurations()}
                  </Box>

                  {currentTariff?.discount > 0 && (
                    <Box
                      className="
                        border bg-purple-500 text-white shadow-md rounded-lg
                        font-bold flex items-center justify-center
                        px-3 py-1
                        text-xs sm:text-sm md:text-base
                        whitespace-nowrap
                        w-[60px] sm:w-auto
                      "
                    >
                      {t('bundles.unitDiscount', { discount: currentTariff?.discount })}
                    </Box>
                  )}
                </Box>

                <Box className="mt-4 md:mt-8">
                  <Box className="flex items-center gap-2">
                    <Typography variant="h3" className="text-lg text-custom-gray">
                      {unit.name}
                    </Typography>

                    {features.length > 0 && (
                      <Box className="flex items-center gap-1">
                        {features.map((feature: any) => {
                          const featureText = getFeatureText(feature, currentTariff?.limitations);

                          const renderFeatureText =
                            feature.slug === 'auto-discount'
                              ? featureText.split('<----->').map((part, index) => (
                                  <Typography variant="body2" key={index} className="mb-2">
                                    {part.trim()}
                                  </Typography>
                                ))
                              : featureText;

                          const featureTooltipContent = (
                            <Box>
                              <Typography variant="caption" className="font-extrabold block mb-1">
                                {t('subscriptions.limit')}
                              </Typography>

                              <Typography variant="body2">{renderFeatureText}</Typography>
                            </Box>
                          );

                          return (
                            <Tooltip key={feature.name} title={featureTooltipContent}>
                              <Box className="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer text-custom-gray hover:text-gray-700 transition-colors">
                                <Iconify icon="eva:info-outline" width={16} />
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                  <Box className="mt-2">
                    <Typography className="text-md  font-medium">
                      {t('bundles.total')}
                      {fNumber(
                        currentTariff.discountPrice
                          ? currentTariff.discountPrice
                          : currentTariff.price
                      )}{' '}
                      {currencySymbol}
                    </Typography>

                    <Typography variant="h2" className="my-1">
                      {fNumber(currentTariff?.minimalPeriodPrice)} {currencySymbol}/{' '}
                      {t('bundles.mon')}
                    </Typography>

                    {(currentTariff.discountPrice || currentTariff.periodicDiscount) && (
                      <Typography variant="h5" className="text-gray-400 line-through">
                        {fNumber(currentTariff?.originalMinimalPeriodPrice)} {currencySymbol}/{' '}
                        {t('bundles.mon')}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {unit.description && (
                  <Box className="mt-2 text-black">
                    <Typography variant="body2" className="text-[12px] md:text-sm text-black">
                      {unit.description}
                    </Typography>
                  </Box>
                )}

                <Box className="mt-auto pt-6 flex items-center gap-4">
                  <Button
                    variant="contained"
                    href="/seller/billing/tariffs"
                    color="success"
                    className="rounded-xl py-3 px-7 font-bold text-sm normal-case"
                  >
                    {t('bundles.payButton')}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
